import React, { useState, useEffect } from 'react'
import { API_BASE } from '../supabaseClient'

export default function ReviewQueue({ session }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [overrideModal, setOverrideModal] = useState(null)
  const [overrideForm, setOverrideForm] = useState({ prediction: '', reason: '', notes: '' })

  useEffect(() => {
    fetchQueue()
  }, [])

  const getHeaders = () => {
    const token = session?.access_token
    return token
      ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      : { 'Content-Type': 'application/json' }
  }

  const fetchQueue = async () => {
    try {
      const res = await fetch(`${API_BASE}/v1/validation/review-queue`, { headers: getHeaders() })
      const data = await res.json()
      setItems(data.items || [])
    } catch (err) {
      console.error('Failed to fetch queue:', err)
    }
    setLoading(false)
  }

  const submitOverride = async () => {
    if (!overrideModal) return
    try {
      const res = await fetch(`${API_BASE}/v1/validation/validate/override`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          validation_id: overrideModal.validation_id,
          reviewer_id: session?.user?.id || 'reviewer-001',
          override_prediction: overrideForm.prediction ? parseFloat(overrideForm.prediction) : null,
          override_reason: overrideForm.reason,
          notes: overrideForm.notes,
        }),
      })
      if (res.ok) {
        setOverrideModal(null)
        setOverrideForm({ prediction: '', reason: '', notes: '' })
        fetchQueue()
      }
    } catch (err) {
      console.error('Override failed:', err)
    }
  }

  if (loading) return <div className="text-center py-8 text-gray-500">Loading...</div>

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Review Queue</h1>

      {items.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border p-8 text-center text-gray-500">
          No items pending review.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map(item => (
            <div key={item.id} className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-mono text-sm text-gray-600">{item.validation_id}</p>
                  <p className="text-lg font-semibold mt-1">{item.target_variable || 'N/A'}</p>
                  <div className="flex space-x-4 mt-2 text-sm text-gray-500">
                    <span>Type: {item.input_type}</span>
                    <span>Confidence: {item.confidence?.toFixed(2)}</span>
                    <span>Priority: {item.priority}</span>
                  </div>
                </div>
                <button
                  onClick={() => setOverrideModal(item)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
                >
                  Review & Override
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {overrideModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-4">Apply Override</h2>
            <p className="text-sm text-gray-500 mb-4">
              Validation: {overrideModal.validation_id?.slice(0, 8)}...
            </p>
            <div className="space-y-3">
              <input
                type="number"
                placeholder="Override prediction (optional)"
                value={overrideForm.prediction}
                onChange={e => setOverrideForm(f => ({ ...f, prediction: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md"
              />
              <input
                type="text"
                placeholder="Override reason"
                value={overrideForm.reason}
                onChange={e => setOverrideForm(f => ({ ...f, reason: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md"
                required
              />
              <textarea
                placeholder="Review notes"
                value={overrideForm.notes}
                onChange={e => setOverrideForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md"
                rows={3}
                required
              />
            </div>
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => setOverrideModal(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={submitOverride}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Submit Override
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
